import { useState } from "react";
import { ArrowLeft, Mail, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { AuthRepository } from "../infrastructure/repositories/AuthRepository";
import { useTheme } from "next-themes";

interface ForgotPasswordPageProps {
  onNavigate: (page: string) => void;
}

export function ForgotPasswordPage({ onNavigate }: ForgotPasswordPageProps) {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const authRepository = new AuthRepository();

  const handleSendNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const result = await authRepository.forgotPassword(email);

      if (result?.status === 1) {
        setShowPopup(true);

        setTimeout(() => {
          setShowPopup(false);
          onNavigate("login");
        }, 2500);
      } else {
        throw new Error(result?.msg || "Failed to send new password.");
      }
    } catch (error) {
      const errMsg =
        error instanceof Error ? error.message : "Unexpected error occurred";
      console.error("Forgot password error:", error);
      setErrorMessage(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img src={logoSrc} alt="Swimnerd Logo" className="h-16 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Forgot Password?
          </h1>
          <p className="text-gray-600 text-sm">
            Enter your registered email below, and we’ll send you a new password.
          </p>
        </div>

        {/* Form Card */}
        <Card className="shadow-xl rounded-2xl overflow-hidden">
          <CardHeader className="bg-indigo-50 px-6 py-4">
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <ArrowLeft
                className="w-5 h-5 cursor-pointer text-indigo-600 hover:text-indigo-800"
                onClick={() => onNavigate("signin")}
              />
              Forgot Password
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6 py-6">
            <form onSubmit={handleSendNewPassword} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your registered email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              {errorMessage && (
                <Alert className="bg-red-50 border-red-200 text-red-700">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2"
              >
                {isSubmitting ? <Loader2 className="animate-spin w-4 h-4" /> : null}
                {isSubmitting ? "Sending..." : "Send New Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Back to Sign In */}
        <div className="mt-6 text-center">
          <Button
            variant="ghost"
            onClick={() => onNavigate("signin")}
            className="flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Sign In</span>
          </Button>
        </div>
      </div>

      {/* Success Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 text-center max-w-xs mx-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              New Password Sent!
            </h2>
            <p className="text-gray-600 text-sm">
              Please check your email for your new login password.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
